import { BroadcastEmptyState } from '../BroadcastEmptyState';
import { useBroadcasts } from '../../queries';
import { BroadcastList } from '../../broadcasts/components/BroadcastList';
import { createIconElement } from '@/lib';
import IcProductEmails from '@/assets/icons/ic-product-collect-emails.svg';
import { BroadcastsTabSkeleton } from './BroadcastsTabSkeleton';
import { StanButton } from '@/components';
import { useStanRouter } from '@/hooks/useStanRouter';
import { AnimatePresence, motion } from 'motion/react';
import IcRightArrow from '@/assets/icons/ic-arrow-right.svg';
import IcUser1 from '@/assets/icons/ic-user-01.svg';

interface BroadcastsTabProps {
    isEmailFlowsAllowed: boolean;
    isUserLoading: boolean;
}

/**
 * Broadcasts tab content component
 * Displays user-triggered broadcasts (email campaigns) with pagination
 */
export const BroadcastsTab = ({ isEmailFlowsAllowed, isUserLoading }: BroadcastsTabProps) => {
    const { broadcasts, total, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useBroadcasts();
    const { push } = useStanRouter();

    // Show banner when broadcasts are empty or user doesn't have email flows allowed
    // Only show after loading is complete to avoid flashing banner during initial fetch
    const showBanner = !isLoading && (total === 0 || !isEmailFlowsAllowed);

    return (
        <>
            {/* Banner */}
            <AnimatePresence>
                {showBanner && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto', transition: { delay: 1 } }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className='overflow-hidden'
                    >
                        <div className='bg-bg-muted flex flex-col items-start gap-4 rounded-lg p-4 sm:flex-row sm:items-center sm:justify-between'>
                            <div className='flex items-center gap-3'>
                                <div className='flex shrink-0 items-center justify-center max-md:hidden'>
                                    {createIconElement(IcUser1, {
                                        className: 'size-6',
                                    })}
                                </div>
                                <div>
                                    <p className='text-dark text-body-md font-semibold'>Already have an Email List?</p>
                                    <p className='text-gray text-body-sm'>
                                        You can import your existing Email List through your Customers tab!
                                    </p>
                                </div>
                            </div>
                            <StanButton
                                name='broadcasts-go-to-customers-button'
                                onClick={() => push('/audience')}
                                icon={createIconElement(IcRightArrow, { className: 'size-5!' })}
                                className='w-full sm:w-auto'
                                variant='outline'
                            >
                                Go to My Customers
                            </StanButton>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Content */}
            <div className='space-y-6'>
                {/* Empty State or Broadcasts List */}
                {isUserLoading || isLoading ? (
                    <BroadcastsTabSkeleton />
                ) : !isEmailFlowsAllowed ? (
                    <BroadcastEmptyState variant='upgrade' />
                ) : broadcasts.length === 0 ? (
                    <BroadcastEmptyState
                        variant='send-email'
                        icon={createIconElement(IcProductEmails, { className: 'size-5!' })}
                    />
                ) : (
                    <>
                        <BroadcastList broadcasts={broadcasts} />

                        {/* Load more pagination */}
                        {hasNextPage && (
                            <button
                                type='button'
                                onClick={() => fetchNextPage()}
                                disabled={isFetchingNextPage}
                                className='text-label-sm-regular text-secondary hover:text-dark cursor-pointer p-2.5 transition-colors disabled:opacity-50'
                            >
                                {isFetchingNextPage ? (
                                    'Loading...'
                                ) : (
                                    <>
                                        Displaying {broadcasts.length} of {total} · Load more ↓
                                    </>
                                )}
                            </button>
                        )}
                    </>
                )}
            </div>
        </>
    );
};
